import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { setCellAttr } from '@tiptap/pm/tables';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

/**
 * Ensures there's always a paragraph after a table that ends the document.
 * Without this, users can't click below the table to add prose — the table
 * is the last block and there's no node beneath it to receive the cursor.
 *
 * Surgical: only fires when the doc's last child is a table. Doesn't force a
 * trailing paragraph for documents ending in a heading / list / etc — those
 * already let you press Enter at end-of-line to extend.
 */
export const TableTrailingParagraph = Extension.create({
  name: 'tableTrailingParagraph',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('tableTrailingParagraph'),
        appendTransaction: (_trs, _oldState, newState) => {
          const { doc, tr, schema } = newState;
          const last = doc.lastChild;
          if (!last || last.type.name !== 'table') return null;
          const paragraph = schema.nodes.paragraph;
          if (!paragraph) return null;
          return tr.insert(doc.content.size, paragraph.create());
        },
      }),
    ];
  },
});

/**
 * Cell-level cosmetic attrs added on top of the stock table-cell / table-header
 * extensions. `backgroundColor` is null|hex; `align` is 'left'|'center'|'right'|null.
 *
 * **Round-trip note.** renderHTML emits BOTH the inline `style` (so browsers
 * paint it natively) AND `data-bg` / `data-align` (so Turndown's `merged-table`
 * rule in `lib/markdown.ts` can cheaply detect "this table needs raw HTML"
 * without parsing inline style strings). parseHTML reads either form, so
 * pasted Google-Docs HTML and our own re-imported markdown both round-trip.
 */
export type CellAlign = 'left' | 'center' | 'right' | null;

const sharedAttrs = {
  backgroundColor: {
    default: null as string | null,
    parseHTML: (el: HTMLElement) => {
      const dataBg = el.getAttribute('data-bg');
      if (dataBg) return dataBg;
      const styleBg = el.style?.backgroundColor;
      return styleBg && styleBg !== 'transparent' ? styleBg : null;
    },
    renderHTML: (attrs: Record<string, any>) => {
      const c = attrs.backgroundColor as string | null | undefined;
      if (!c) return {};
      return {
        'data-bg': c,
        style: `background-color: ${c}`,
      };
    },
  },
  align: {
    default: null as CellAlign,
    parseHTML: (el: HTMLElement) => {
      const raw =
        el.getAttribute('data-align') ?? el.style?.textAlign ?? '';
      return raw === 'left' || raw === 'center' || raw === 'right' ? raw : null;
    },
    renderHTML: (attrs: Record<string, any>) => {
      const a = attrs.align as CellAlign;
      if (!a) return {};
      return {
        'data-align': a,
        style: `text-align: ${a}`,
      };
    },
  },
};

// `addAttributes` emits a single object; `renderHTML` for stock TableCell
// merges these via mergeAttributes. When two of our attrs both want to
// contribute a `style` key, TipTap's mergeAttributes concatenates them with
// `; ` — so a cell with both bg and align ends up with
// `style="background-color: …; text-align: …"`. Verified against
// @tiptap/core/dist mergeAttributes behaviour.
export const TableCellEx = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...sharedAttrs,
    };
  },
  addCommands() {
    return {
      ...(this.parent?.() ?? {}),
      setCellBackground:
        (color: string | null) =>
        ({ state, dispatch }: { state: any; dispatch: any }) =>
          setCellAttr('backgroundColor', color)(state, dispatch),
      setCellAlign:
        (align: CellAlign) =>
        ({ state, dispatch }: { state: any; dispatch: any }) =>
          setCellAttr('align', align)(state, dispatch),
    } as any;
  },
});

export const TableHeaderEx = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      ...sharedAttrs,
    };
  },
});
